import ReactDOM from "react-dom";
import { useState, useRef, useMemo, createContext, useContext, useEffect } from "react";
import { usePopper } from "react-popper";
import useKeyPress from "../../utils/hooks/use-key-press";
import useOnClickOutside from "../../utils/hooks/use-click-outside";
import cx from "../../utils/tailwind-merge";

const PopperContext = createContext();

function Popper(props) {
  const {
    onOutsideClick,
    onEscapeClick,
    className,
    children,
    isOpen,
    setIsOpen,
    placement = "bottom-end",
    portal = false,
    setRef,
    inputRef,
    arrow = false,
    customAnchorElement = null,
  } = props;

  const [anchorElement, setAnchorElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const { styles, attributes, state } = usePopper(customAnchorElement || anchorElement, popperElement, {
    placement: placement,
    modifiers: [{ name: "arrow", options: { element: arrowElement } }],
  });
  const visible = state?.modifiersData?.hide?.isReferenceHidden;
  const popperRef = useRef(null);

  useOnClickOutside(popperRef, (event) => {
    if (inputRef?.current) {
      if (inputRef.current.contains(event.target)) return;
    }
    onOutsideClick && onOutsideClick();
  });

  useKeyPress(document, "Escape", (e) => {
    onEscapeClick && onEscapeClick(e);
  });
  useEffect(() => {
    if (visible) {
      // setIsOpen(false);
    }
    popperRef.current = popperElement;
    setRef && setRef(popperElement);
    return () => {
      popperRef.current = null;
      setRef && setRef(null);
    };
  }, [inputRef, popperElement, setIsOpen, setRef, visible]);

  const value = useMemo(() => {
    return {
      arrow,
      styles,
      isOpen,
      portal,
      popperRef,
      attributes,
      popperElement,
      setPopperElement,
      setAnchorElement,
      setArrowElement,
    };
  }, [arrow, attributes, isOpen, popperElement, portal, styles]);

  return (
    <PopperContext.Provider value={value}>
      <div className={cx("popper relative", className)}>{children}</div>
    </PopperContext.Provider>
  );
}

function PopperTrigger(props) {
  const { children } = props;
  const { setAnchorElement } = useContext(PopperContext);

  return <>{children({ setAnchorElement })}</>;
}

function PopperContent(props) {
  const { children, className } = props;
  const { arrow, portal, styles, attributes, isOpen, setPopperElement, setArrowElement } = useContext(PopperContext);

  const content = isOpen ? (
    <div ref={setPopperElement} style={styles.popper} {...attributes.popper} className={cx("popper-content !z-[9999]", className)} tabIndex="-1" autoFocus>
      {arrow ? <div ref={(ref) => setArrowElement(ref)} style={styles.arrow} /> : null}
      {children}
    </div>
  ) : null;

  if (portal) {
    return <Portal>{content}</Portal>;
  }

  return <>{content}</>;
}

Popper.Trigger = PopperTrigger;
Popper.Content = PopperContent;

export default Popper;

function Portal(props) {
  const { parent, children } = props;
  return ReactDOM.createPortal(children, parent || document.body);
}
